/**
 * SoundToggle — small mute button + volume slider, top-right corner.
 *
 * Reads/writes the engine's persisted mute + volume. Kept intentionally
 * tiny; it's a global overlay control alongside the connection dot.
 */

import { useState, type ReactElement } from "react";
import { useSound } from "./SoundProvider.js";

export function SoundToggle(): ReactElement {
  const sound = useSound();
  const [muted, setMutedState] = useState<boolean>(sound.isMuted());
  const [volume, setVolumeState] = useState<number>(sound.getVolume());

  const toggleMute = (): void => {
    const next = !muted;
    sound.setMuted(next);
    setMutedState(next);
    // A tiny confirmation blip when unmuting (also nudges the audio unlock).
    if (!next) sound.play("queuePop");
  };

  const onVolume = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const v = Number(e.target.value) / 100;
    sound.setVolume(v);
    setVolumeState(v);
  };

  return (
    <div
      data-testid="sound-toggle"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        background: "rgba(20,26,34,0.7)",
        borderRadius: 6,
        padding: "4px 8px",
      }}
    >
      <button
        type="button"
        data-testid="sound-mute"
        aria-label={muted ? "Unmute" : "Mute"}
        aria-pressed={muted}
        onClick={toggleMute}
        style={{
          background: "transparent",
          border: "none",
          color: "#dfe6ef",
          cursor: "pointer",
          fontSize: 16,
          lineHeight: 1,
          padding: 0,
        }}
      >
        {muted ? "🔇" : "🔊"}
      </button>
      <input
        type="range"
        data-testid="sound-volume"
        aria-label="Volume"
        min={0}
        max={100}
        value={Math.round(volume * 100)}
        onChange={onVolume}
        disabled={muted}
        style={{ width: 72 }}
      />
    </div>
  );
}
